import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import styles from './AsteroidDefense.module.css';

const AsteroidDefense = () => {
    const canvasRef = useRef(null);

    const [gameState, setGameState] = useState('START'); // START, PLAYING, GAME_OVER
    const gameStateRef = useRef('START');
    const [score, setScore] = useState(0);
    const [shield, setShield] = useState(3);
    const [level, setLevel] = useState(1);
    const [highScore, setHighScore] = useState(0);
    const [targetMultiplier, setTargetMultiplier] = useState(1);
    const [paused, setPaused] = useState(false);

    // Game Objects Refs
    const asteroidsRef = useRef([]);
    const projectilesRef = useRef([]);
    const particlesRef = useRef([]);
    const cratersRef = useRef([]);
    const citiesRef = useRef([]);
    const turretAngleRef = useRef(0);
    const lastGreenShotTimeRef = useRef(0);
    const lastRedShotTimeRef = useRef(0);
    const scoreRef = useRef(0);
    const shieldRef = useRef(3);
    const levelRef = useRef(1);
    const frameIdRef = useRef(0);
    const mousePosRef = useRef({ x: 0, y: 0 });
    const gameTimeRef = useRef(0);

    // Constants
    const TURRET_SIZE = 40;
    const GREEN_LASER_COOLDOWN = 150;
    const RED_LASER_COOLDOWN = 1000;
    const BASE_SPAWN_MS = 2000;
    // Level Timer Logic (5 seconds per level for testing)
    const LEVEL_DURATION = 5000;

    // Colors
    const COLOR_BG = '#111';
    const COLOR_GROUND = '#5d4037';
    const COLOR_TURRET = '#bdc3c7';
    const COLOR_SHIELD = '#3498db';
    const COLOR_ASTEROID = ['#555', '#444', '#333'];

    const setGameStateSafe = (newState) => {
        setGameState(newState);
        gameStateRef.current = newState;
    };

    const initCities = (width, height) => {
        // Create 2 cities on left, 2 on right
        const y = height - 50;
        return [
            { x: width * 0.15, y, w: 40, h: 20, alive: true, color: '#9b59b6' },
            { x: width * 0.30, y, w: 30, h: 25, alive: true, color: '#f1c40f' },
            { x: width * 0.70, y, w: 35, h: 18, alive: true, color: '#e67e22' },
            { x: width * 0.85, y, w: 25, h: 30, alive: true, color: '#34495e' }
        ];
    };

    const startGame = () => {
        if (!canvasRef.current) return;
        const { width, height } = canvasRef.current;

        setGameStateSafe('PLAYING');
        setScore(0);
        setShield(3);
        setLevel(1);

        scoreRef.current = 0;
        shieldRef.current = 3;
        levelRef.current = 1;

        asteroidsRef.current = [];
        projectilesRef.current = [];
        particlesRef.current = [];
        cratersRef.current = [];
        citiesRef.current = initCities(width, height);

        gameTimeRef.current = 0;
        lastGreenShotTimeRef.current = 0;
        lastRedShotTimeRef.current = 0;
    };

    const handleMouseMove = (e) => {
        if (!canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        mousePosRef.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    };

    // Level Configuration (Slowed Down)
    const LEVELS_CONFIG = {
        1: { spawnRate: 3000, name: "Meteor Shower", shieldRegen: false },
        2: { spawnRate: 2500, name: "Asteroid Field", shieldRegen: false },
        3: { spawnRate: 2000, name: "Heavy Rain", shieldRegen: false },
        4: { spawnRate: 1800, name: "Kessler Syndrome", shieldRegen: false },
        5: { spawnRate: 1500, name: "Orbital Chaos", shieldRegen: false },
        6: { spawnRate: 4000, name: "The Gargantua", boss: true }, // Initial boss spawn slow
        7: { spawnRate: 1200, name: "Aftermath", shieldRegen: true },
        8: { spawnRate: 1000, name: "Deep Space", shieldRegen: false },
        9: { spawnRate: 900, name: "Final Wave", shieldRegen: false },
        10: { spawnRate: 800, name: "Extinction Event", shieldRegen: false },
        // Levels 11+ are Alien Invasions
    };
    const MAX_LEVEL = 15; // Extended for aliens

    const spawnAlien = (width) => {
        const startX = Math.random() * width;
        asteroidsRef.current.push({
            x: startX,
            y: -30,
            vx: (Math.random() - 0.5) * 2, // Initial drift
            vy: 2, // Faster than asteroids
            size: 25 * targetMultiplier,
            health: 3, // Takes 3 green shots or 1 red
            maxHealth: 3,
            type: 'alien',
            color: '#000000', // Black
            rotation: 0,
            rotationSpeed: 0,
            zigZagOffset: Math.random() * 100
        });
    };

    const spawnAsteroid = (width) => {
        const rand = Math.random();
        let type, size, health;

        // Difficulty scaling by level
        const l = levelRef.current;
        let pLarge = 0.2 + (l * 0.02);
        let pMedium = 0.3 + (l * 0.02);

        if (rand < pLarge) {
            // Large
            type = 'large';
            size = 50 * targetMultiplier;
            health = 3;
        } else if (rand < pLarge + pMedium) {
            // Medium
            type = 'medium';
            size = 35 * targetMultiplier;
            health = 2;
        } else {
            // Small
            type = 'small';
            size = 20 * targetMultiplier;
            health = 1;
        }

        // Target logic: 50% chance to aim at turret, 50% random ground
        const startX = Math.random() * width;
        const startY = -size;
        const targetY = canvasRef.current ? canvasRef.current.height : 600;

        let targetX;
        if (Math.random() < 0.5) {
            // Aim at Turret (Center) with slight variance
            targetX = width / 2 + (Math.random() - 0.5) * 50;
        } else {
            // Aim at Random Ground Location
            targetX = Math.random() * width;
        }

        const angle = Math.atan2(targetY - startY, targetX - startX);
        const speed = 1.5 + (gameTimeRef.current / 60000); // Speed scales with time

        asteroidsRef.current.push({
            x: startX,
            y: startY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            size: size,
            health: health,
            maxHealth: health,
            color: COLOR_ASTEROID[Math.floor(Math.random() * COLOR_ASTEROID.length)],
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.1,
            type: type
        });
    };

    const createParticles = (x, y, color, count) => {
        for (let i = 0; i < count; i++) {
            particlesRef.current.push({
                x: x,
                y: y,
                vx: (Math.random() - 0.5) * 5,
                vy: (Math.random() - 0.5) * 5,
                size: Math.random() * 3 + 1,
                color: color,
                life: 1
            });
        }
    };

    const createExplosion = (x, y, radius, damage) => {
        projectilesRef.current.push({
            x: x,
            y: y,
            radius: radius,
            damage: damage,
            type: 'explosion',
            life: 0.3, // Duration of explosion animation
            maxLife: 0.3
        });
    };

    const handleAsteroidDestroyed = (a) => {
        if (a.dead) return; // Prevention
        a.dead = true;
        createParticles(a.x, a.y, a.color, 10);
        createExplosion(a.x, a.y, a.size * 1.5, 0); // Visual explosion
        scoreRef.current += a.maxHealth * 10;
        setScore(scoreRef.current);

        // Splitting Logic - REMOVED for Boss
        if (a.generation === 1) {
            // Large Fragment -> 3 Medium
            spawnFragment(a.x, a.y, 25 * targetMultiplier, 'medium', 3, 2);
        } else if (a.generation === 2) {
            // Medium Fragment -> 2 Small
            spawnFragment(a.x, a.y, 15 * targetMultiplier, 'small', 2, 3);
        }

        // Debris
        if (a.size > 30) {
            createParticles(a.x, a.y, '#e67e22', 10);
        }
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        if (gameStateRef.current !== 'PLAYING' || paused) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const turretX = canvas.width / 2;
        const turretY = canvas.height - 30;

        const now = Date.now();

        if (e.button === 0) { // Left click for green laser
            if (now - lastGreenShotTimeRef.current > GREEN_LASER_COOLDOWN) {
                lastGreenShotTimeRef.current = now;
                projectilesRef.current.push({
                    sx: turretX,
                    sy: turretY,
                    ex: mouseX,
                    ey: mouseY,
                    color: '#2ecc71',
                    width: 2,
                    damage: 1, // Green laser does 1 damage
                    type: 'beam',
                    life: 0.1 // Beam duration
                });
            }
        } else if (e.button === 2) { // Right click for red laser
            if (now - lastRedShotTimeRef.current > RED_LASER_COOLDOWN) {
                lastRedShotTimeRef.current = now;
                projectilesRef.current.push({
                    sx: turretX,
                    sy: turretY,
                    ex: mouseX,
                    ey: mouseY,
                    color: '#e74c3c',
                    width: 5,
                    damage: 5, // Red laser does 5 damage
                    type: 'beam',
                    life: 0.1
                });
            }
        }
    };

    const handleContextMenu = (e) => {
        e.preventDefault(); // Prevent right-click context menu
    };

    const update = useCallback(() => {
        if (gameStateRef.current === 'GAME_WON' || gameStateRef.current === 'GAME_OVER') {
            // Just render static scene or stop loop? 
            // Ideally we stop loop, but let's just return to save cycles
            return;
        }
        if (gameStateRef.current !== 'PLAYING') {
            cancelAnimationFrame(frameIdRef.current);
            return;
        }
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        if (paused) {
            render(ctx, width, height);
            frameIdRef.current = requestAnimationFrame(update);
            return;
        }

        gameTimeRef.current += 16;
        const totalGameTime = gameTimeRef.current;

        // Level Timer Logic
        const timeInLevel = totalGameTime % LEVEL_DURATION;
        const currentLevelIndex = Math.floor(totalGameTime / LEVEL_DURATION) + 1;

        if (currentLevelIndex > MAX_LEVEL) {
            setGameStateSafe('GAME_WON');
            return;
        }

        if (currentLevelIndex !== levelRef.current) {
            levelRef.current = currentLevelIndex;
            setLevel(currentLevelIndex);
            createParticles(width / 2, height / 2, '#f1c40f', 50);

            // Check for Boss Spawn from Config
            if (LEVELS_CONFIG[currentLevelIndex]?.boss) {
                asteroidsRef.current.push({
                    x: width / 2,
                    y: -150,
                    vx: 0,
                    vy: 0.3, // Slightly faster than before (0.15 -> 0.3)
                    size: 100 * targetMultiplier,
                    health: 12, // 12 Green Shots to split
                    maxHealth: 12,
                    type: 'boss',
                    color: '#000',
                    rotation: 0,
                    rotationSpeed: 0.01
                });
            }
            // Optional: Heal shield between levels if config says so
            if (LEVELS_CONFIG[currentLevelIndex]?.shieldRegen && shieldRef.current < 3) {
                shieldRef.current += 1;
                setShield(shieldRef.current);
            }
        }

        const levelConfig = LEVELS_CONFIG[currentLevelIndex] || { spawnRate: 800, name: "Alien Invasion" };

        // Spawning Logic
        // Check if boss is alive
        const bossActive = asteroidsRef.current.some(a => a.type === 'boss');

        // Pause spawning for first 3 seconds of level (Break)
        // Also disable normal spawns if boss is alive?? No, user didn't say so. keeping logic simple.
        if (timeInLevel > 3000) {
            let spawnRate = levelConfig.spawnRate || 2000;
            if (bossActive) {
                spawnRate *= 5; // TRICKLE if boss is present
            }

            if (gameTimeRef.current % spawnRate < 16) {
                // Don't spawn normal stuff if Boss is active? 
                // User wants chaos, let's keep spawning but maybe slower?
                // Current implementation spawns normal asteroids alongside boss
                if (currentLevelIndex > 10) {
                    spawnAlien(width);
                } else {
                    spawnAsteroid(width);
                }
            }
        }

        // Turret Angle
        const turretX = width / 2;
        const turretY = height - 30;
        const dx = mousePosRef.current.x - turretX;
        const dy = mousePosRef.current.y - turretY;
        turretAngleRef.current = Math.atan2(dy, dx);

        // Update Projectiles
        projectilesRef.current.forEach(p => {
            p.life -= 0.016;

            if (p.type === 'beam' && p.life > 0) {
                // Check for collisions with asteroids
                asteroidsRef.current.forEach(a => {
                    if (a.dead) return;

                    // Improved Collision for Beams (Line Segment to Circle)
                    // Logic: Find closest point on line segment to circle center
                    const dx = p.ex - p.sx;
                    const dy = p.ey - p.sy;
                    const l2 = dx * dx + dy * dy;

                    if (l2 === 0) return; // Zero length beam

                    // t = projection of circle center onto line
                    let t = ((a.x - p.sx) * dx + (a.y - p.sy) * dy) / l2;
                    t = Math.max(0, Math.min(1, t)); // Clamp to segment

                    const closestX = p.sx + t * dx;
                    const closestY = p.sy + t * dy;

                    const distSq = (a.x - closestX) ** 2 + (a.y - closestY) ** 2;

                    if (distSq < a.size * a.size) {
                        // Hit!
                        a.health -= p.damage;
                        // Hit effect at closest point
                        createParticles(closestX, closestY, p.color, 5);

                        // Prevent multiple hits from same beam frame? 
                        // Beam life is short, so maybe okay. 
                        // But we should disable this beam hitting THIS asteroid again?
                        // Current logic allows continuous dmg if beam persists, which is fine for "beam".
                        // Wait, user wants instant shot. p.life is 0.1.
                        // We should probably mark beam as "used" if it's a single shot projectile logic,
                        // but visual beam lingers. 
                        // Simplest fix for "Edges": The previous dot product math was correct for infinite line,
                        // but maybe issue was precision or `dist` check.
                        // The clamping `t` above handles segment correctly.

                        if (a.health <= 0) {
                            a.dead = true;
                            handleAsteroidDestroyed(a);
                        }

                        // If it's a single-hit weapon (like a bullet), remove it.
                        // If it's a "laser stream", keep it.
                        // Current logic: p.life = 0 on hit means single target per shot.
                        // If we want penetration, remove this line.
                        p.life = 0;
                    }
                });
            }
        });
        projectilesRef.current = projectilesRef.current.filter(p => p.life > 0);

        // Update Asteroids & Aliens
        asteroidsRef.current.forEach(a => {
            if (a.type === 'alien') {
                // Zig Zag Logic
                // Alien Movement Smoothing
                a.y += a.vy;
                // Use a smoother sine wave for zig-zag
                a.x += Math.sin((a.y * 0.02) + a.zigZagOffset) * 2;

                // Homing: Smooth turn towards turret
                const dx = turretX - a.x;
                // gentle nudge towards center
                a.x += dx * 0.005;
            } else {
                a.x += a.vx;
                a.y += a.vy;
                a.rotation += a.rotationSpeed;
            }
        });

        // Collisions
        asteroidsRef.current.forEach(a => {
            if (a.dead) return;

            // Shield
            const shieldRadius = 100 + (shieldRef.current * 20);
            const distToBase = Math.hypot(a.x - turretX, a.y - turretY);

            if (shieldRef.current > 0 && distToBase < shieldRadius + a.size) {
                a.dead = true;
                if (a.type === 'boss') {
                    shieldRef.current = 0;
                    setShield(0);
                    a.health -= 12; // Massive hit if boss touches shield
                } else {
                    createParticles(a.x, a.y, '#3498db', 8);
                    shieldRef.current -= 1;
                    setShield(shieldRef.current);
                }

                // If boss survives shield collision (unlikely with this logic but for safety)
                if (a.health <= 0 && a.type === 'boss') handleAsteroidDestroyed(a);

            }
            else if (a.y + a.size >= height - 30) {
                // Ground
                a.dead = true;
                cratersRef.current.push({ x: a.x, size: a.size });
                createParticles(a.x, height - 30, '#5d4037', 15);

                if (a.type === 'boss') {
                    setGameStateSafe('GAME_OVER');
                    return;
                }

                // City Collision
                let hitCity = false;
                citiesRef.current.forEach(city => {
                    if (!city.alive) return;
                    if (a.x + a.size > city.x && a.x - a.size < city.x + city.w) {
                        city.alive = false;
                        createParticles(city.x + city.w / 2, city.y, city.color, 20);
                        hitCity = true;
                    }
                });

                if (!hitCity && shieldRef.current <= 0) {
                    setGameStateSafe('GAME_OVER');
                } else if (hitCity) {
                    const aliveCities = citiesRef.current.filter(city => city.alive);
                    if (aliveCities.length === 0) {
                        setGameStateSafe('GAME_OVER');
                    }
                }
            }
        });

        asteroidsRef.current = asteroidsRef.current.filter(a => !a.dead);

        // Update Particles
        particlesRef.current.forEach(p => {
            p.x += p.vx;
            p.y += p.vy;
            p.life -= 0.05;
        });
        particlesRef.current = particlesRef.current.filter(p => p.life > 0);

        render(ctx, width, height);

        // Level Text Overlay
        if (timeInLevel < 3000) {
            ctx.save();
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            ctx.fillRect(0, height / 2 - 60, width, 120);

            ctx.fillStyle = '#f1c40f';
            ctx.font = 'bold 48px "Courier New", monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.shadowColor = 'black'; // Fixed shadow color being undefined previously?
            ctx.shadowBlur = 10;

            let configName = LEVELS_CONFIG[currentLevelIndex]?.name;
            if (!configName) configName = currentLevelIndex > 10 ? "ALIEN ATTACK" : `LEVEL ${currentLevelIndex}`;

            ctx.fillText(configName.toUpperCase(), width / 2, height / 2);

            // Subtext for Boss
            if (LEVELS_CONFIG[currentLevelIndex]?.boss) {
                ctx.fillStyle = 'red';
                ctx.font = 'bold 24px "Courier New", monospace';
                ctx.fillText("INCOMING THREAT", width / 2, height / 2 + 40);
            }
            ctx.restore();
        }

        // Timer UI
        ctx.fillStyle = 'white';
        ctx.font = '16px monospace';
        ctx.textAlign = 'right';
        const timeLeft = Math.max(0, (LEVEL_DURATION / 1000) - Math.floor(timeInLevel / 1000));
        ctx.fillText(`Next Level: ${timeLeft}s`, width - 20, 30);
        ctx.textAlign = 'left';
        ctx.fillText(`L${currentLevelIndex}: ${LEVELS_CONFIG[currentLevelIndex]?.name || (currentLevelIndex > 10 ? "Alien Invasion" : '')}`, 20, 30);

        frameIdRef.current = requestAnimationFrame(update);
    }, []);

    useEffect(() => {
        if (gameState === 'PLAYING') {
            frameIdRef.current = requestAnimationFrame(update);
        } else {
            cancelAnimationFrame(frameIdRef.current);
        }
        return () => cancelAnimationFrame(frameIdRef.current);
    }, [gameState, update]);

    const render = (ctx, width, height) => {
        // Clear
        ctx.fillStyle = COLOR_BG;
        ctx.fillRect(0, 0, width, height);

        // Ground
        ctx.fillStyle = COLOR_GROUND;
        ctx.fillRect(0, height - 30, width, 30);

        // Draw craters
        ctx.fillStyle = '#3e2723';
        cratersRef.current.forEach(c => {
            ctx.beginPath();
            ctx.arc(c.x, height - 30, c.size, 0, Math.PI);
            ctx.fill();
        });

        // Cities
        citiesRef.current.forEach(city => {
            if (!city.alive) return;
            ctx.fillStyle = city.color;
            ctx.fillRect(city.x, city.y - city.h + 20, city.w, city.h);
            ctx.fillStyle = 'rgba(255,255,255,0.3)';
            for (let wx = 4; wx < city.w - 4; wx += 8) {
                for (let wy = 4; wy < city.h - 4; wy += 8) {
                    ctx.fillRect(city.x + wx, (city.y - city.h + 20) + wy, 4, 4);
                }
            }
        });

        // Turret Center
        const turretX = width / 2;
        const turretY = height - 30;

        // Turret Barrel
        ctx.save();
        ctx.translate(turretX, turretY);
        ctx.rotate(turretAngleRef.current);
        ctx.fillStyle = COLOR_TURRET;
        ctx.fillRect(0, -5, 30, 10);
        ctx.restore();

        // Turret Base
        ctx.fillStyle = '#7f8c8d';
        ctx.beginPath();
        ctx.arc(turretX, turretY, 20, Math.PI, 0);
        ctx.fill();

        // Particles (Render BEFORE asteroids for "behind" trails, OR AFTER for explosions)
        // Let's split: Trails behind, Explosions in front. For now, all in front looks chaotic/good.
        particlesRef.current.forEach(p => {
            ctx.globalAlpha = p.life;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1.0;
        });

        // Shield
        const shieldRadius = 100 + (shieldRef.current * 20);
        if (shieldRef.current > 0) {
            ctx.strokeStyle = `rgba(52, 152, 219, ${0.2 + (shieldRef.current * 0.15)})`;
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.arc(turretX, height - 30, shieldRadius, Math.PI, 0);
            ctx.stroke();
            ctx.fillStyle = `rgba(52, 152, 219, ${0.05 * shieldRef.current})`;
            ctx.fill();
        }

        // Asteroids & Aliens
        asteroidsRef.current.forEach(a => {
            ctx.save();
            ctx.translate(a.x, a.y);

            if (a.type === 'alien') {
                // Alien Ship Drawing
                ctx.fillStyle = a.color; // Black
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 2;

                // Saucer shape
                ctx.beginPath();
                ctx.ellipse(0, 0, a.size, a.size / 2, 0, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke(); // White outline

                // Dome
                ctx.fillStyle = '#333'; // Dark Gray Dome
                ctx.beginPath();
                ctx.arc(0, -5, a.size / 2, Math.PI, 0);
                ctx.fill();
                ctx.stroke(); // White outline

                // Lights
                ctx.fillStyle = '#f1c40f'; // Yellow lights still look cool on black
                for (let i = -2; i <= 2; i++) {
                    ctx.beginPath();
                    ctx.arc(i * 8, 2, 2, 0, Math.PI * 2);
                    ctx.fill();
                }

            } else {
                // Asteroid Drawing
                ctx.rotate(a.rotation);
                ctx.fillStyle = a.color;

                if (a.hitFlash > 0) {
                    ctx.fillStyle = '#ffffff'; // Flash white on hit
                    a.hitFlash--;
                }

                if (a.type === 'boss') {
                    ctx.strokeStyle = '#e74c3c'; // Red glowing outline for boss
                    ctx.shadowColor = '#c0392b';
                    ctx.shadowBlur = 15;
                    ctx.lineWidth = 3;
                }

                // Draw Rough Asteroid Shape
                ctx.beginPath();
                const segments = 8;
                for (let i = 0; i < segments; i++) {
                    const ang = (i / segments) * Math.PI * 2;
                    const r = a.size;
                    const px = Math.cos(ang) * r;
                    const py = Math.sin(ang) * r;
                    if (i === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }
                ctx.closePath();
                ctx.fill();
                if (a.type === 'boss') {
                    ctx.stroke();
                    ctx.shadowBlur = 0; // Reset
                }
            }
            ctx.restore();

            // Health Bar
            if (a.health < a.maxHealth) {
                ctx.fillStyle = 'red';
                ctx.fillRect(a.x - 10, a.y - a.size - 10, 20, 3);
                ctx.fillStyle = 'green';
                ctx.fillRect(a.x - 10, a.y - a.size - 10, 20 * (a.health / a.maxHealth), 3);
            }
        });

        // Beams / Explosions
        projectilesRef.current.forEach(p => {
            if (p.type === 'beam') {
                ctx.strokeStyle = p.color;
                ctx.lineWidth = p.width;
                ctx.globalAlpha = p.life / 0.1;
                ctx.beginPath();
                ctx.moveTo(p.sx, p.sy);
                ctx.lineTo(p.ex, p.ey);
                ctx.stroke();
                ctx.globalAlpha = 1.0;
            } else if (p.type === 'explosion') {
                ctx.fillStyle = 'rgba(231, 76, 60, 0.3)';
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius * (1 - p.life / p.maxLife), 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#e74c3c';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        });

        // Particles moved to before asteroids for Z-ordering (or let them be on top?)
        // Actually, user wants "clean up". 
        // Rendering particles AFTER projectiles/asteroids puts them ON TOP (explosions).
        // That is usually desired.
        // I moved logic to before shield in a previous chunk. 
        // So I should REMOVE this block here to avoid double rendering.
        // Wait, I shouldn't rely on "I moved it", I must be sure.
        // Yes, I moved it to lines ~600. So I will just delete this block.
    };

    const handleQuit = () => {
        setGameStateSafe('START');
    };

    const sidebarControls = (
        <div style={{ color: 'white' }}>
            <h3 style={{ marginBottom: '15px', fontSize: '1.2rem', color: '#f1c40f' }}>Settings</h3>
            <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: '#ccc' }}>Target Scale</label>
                <select
                    value={targetMultiplier}
                    onChange={(e) => setTargetMultiplier(parseFloat(e.target.value))}
                    style={{
                        padding: '10px',
                        borderRadius: '6px',
                        background: '#2c3e50',
                        color: 'white',
                        border: '1px solid #34495e',
                        width: '100%',
                        cursor: 'pointer',
                        outline: 'none'
                    }}
                >
                    <option value="0.5">Small (Hard)</option>
                    <option value="1">Normal</option>
                    <option value="1.5">Large (Easy)</option>
                    <option value="2">Huge (Casual)</option>
                </select>
            </div>

            {gameState === 'PLAYING' && (
                <button
                    onClick={() => setPaused(!paused)}
                    style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: paused ? '#2ecc71' : '#e67e22',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        width: '100%',
                        textAlign: 'center',
                        marginTop: '10px',
                        boxShadow: '0 4px 0 rgba(0,0,0,0.2)'
                    }}
                >
                    {paused ? "▶ RESUME" : "⏸ PAUSE"}
                </button>
            )}

            <div style={{ marginTop: '30px', fontSize: '0.85rem', color: '#7f8c8d', lineHeight: '1.5' }}>
                <p><strong>Left Click:</strong> Rapid Fire</p>
                <p><strong>Right Click:</strong> Heavy Beam</p>
            </div>
        </div>
    );

    return (
        <GameLayout
            canvasRef={canvasRef}
            onCanvasClick={handleMouseDown}
            gameRunning={gameState === 'PLAYING'}
            onQuit={handleQuit}
            sidebarContent={sidebarControls}
        >
            <div className={styles.container} onContextMenu={handleContextMenu}>

                {gameState === 'START' && (
                    <div className={styles.overlay}>
                        <h1 className={styles.title}>Asteroid Defense</h1>
                        <p style={{ marginBottom: '20px' }}>
                            Right Click: Heavy Red Laser (Slow)<br />
                            Left Click: Rapid Green Laser (Fast)
                        </p>
                        <button className={styles.button} onClick={startGame}>Start Defense</button>
                    </div>
                )}

                {gameState === 'GAME_OVER' && (
                    <div className={styles.overlay}>
                        <h1 className={styles.title} style={{ color: '#e74c3c' }}>CRITICAL FAILURE</h1>
                        <div className={styles.score}>Final Score: {score}</div>
                        <div className={styles.score} style={{ fontSize: '1rem' }}>Level Reached: {level}</div>
                        <button className={styles.button} onClick={startGame}>Re-Initialize</button>
                    </div>
                )}

                {gameState === 'GAME_WON' && (
                    <div className={styles.overlay}>
                        <h1 className={styles.title} style={{ color: '#2ecc71' }}>YOU WON!</h1>
                        <div className={styles.score}>Final Score: {score}</div>
                        <div className={styles.score} style={{ fontSize: '1rem' }}>Earth is Safe... For Now.</div>
                        <button className={styles.button} onClick={startGame}>Play Again</button>
                    </div>
                )}

                {gameState === 'PLAYING' && (
                    <>
                        <div className={styles.hud}>
                            <div className={styles.score} style={{ fontSize: '1rem', marginTop: '10px' }}>Level {level}</div>
                            <div className={styles.shieldBar} style={{
                                marginTop: '5px',
                                width: '200px',
                                height: '10px',
                                background: '#333',
                                borderRadius: '5px',
                                overflow: 'hidden',
                                border: '1px solid #555'
                            }}>
                                <div
                                    className={styles.shieldFill}
                                    style={{
                                        width: `${(shield / 3) * 100}%`,
                                        height: '100%',
                                        background: '#3498db',
                                        transition: 'width 0.2s ease'
                                    }}
                                />
                            </div>
                            <div className={styles.laserInfo} style={{ marginTop: '5px', fontSize: '0.8rem', opacity: 0.8 }}>
                                L-Click: Rapid | R-Click: Heavy
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* Invis overlay to catch events if needed */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: gameState === 'PLAYING' ? 'auto' : 'none',
                    zIndex: 10
                }}
                onMouseMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                onContextMenu={handleContextMenu}
            />
        </GameLayout>
    );
};

export default AsteroidDefense;
